import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaShip,
  FaUsersCog,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaRedo,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaBox,
  FaMoneyBillWave,
  FaShieldAlt,
  FaTruck,
  FaInfoCircle,
  FaPrint,
  FaPauseCircle,
  FaPlayCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { api, getStoredUser, logout } from "../../services/authServices";
import ConfirmModal from "../../components/ConfirmModal";

const STATUS_META = {
  Packaging: { color: "#FF9F0A", bg: "rgba(255,159,10,0.12)" },
  Shipped: { color: "#D4AF37", bg: "rgba(212,175,55,0.12)" },
  "In Transit": { color: "#D4AF37", bg: "rgba(212,175,55,0.12)" },
  "Out for Delivery": { color: "#2196F3", bg: "rgba(33,150,243,0.12)" },
  Delivered: { color: "#34C759", bg: "rgba(52,199,89,0.12)" },
  Delayed: { color: "#E53E3E", bg: "rgba(229,62,62,0.12)" },
  Cancelled: { color: "#8E8E93", bg: "rgba(142,142,147,0.12)" },
};

const PAGE_SIZE = 7;

/* ─── Component ───────────────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser] = useState(getStoredUser());

  // Modal state
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Route control (pause/reroute) state
  const [showPauseForm, setShowPauseForm] = useState(false);
  const [routeAddress, setRouteAddress] = useState({
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [routeReason, setRouteReason] = useState("");
  const [resumeNote, setResumeNote] = useState("");
  const [routeActionLoading, setRouteActionLoading] = useState(false);
  const [routeActionError, setRouteActionError] = useState("");

  // Confirm/notice modal state (replaces window.confirm / alert)
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notice, setNotice] = useState(null); // { title, message }

  // Print Modal state
  const [printShipment, setPrintShipment] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  /* ── Fetch shipments ──────────────────────────────────────────────────── */
  const fetchShipments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/api/user/shipment/all");
      const shipmentsData = Array.isArray(data)
        ? data
        : data.data?.shipments || data.data || [];
      setShipments(shipmentsData);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load shipments. Please try again.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  /* ── Fetch single shipment for modal ──────────────────────────────────── */
  const fetchShipmentDetails = async (id) => {
    setIsModalLoading(true);
    setModalError(null);
    try {
      const { data } = await api.get(`/api/user/shipment/${id}`);
      setSelectedShipment(data.data || data);
      setIsModalOpen(true);
      setShowPauseForm(false);
      setRouteActionError("");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load shipment details.";
      setModalError(message);
    } finally {
      setIsModalLoading(false);
    }
  };

  /* ── Fetch shipment for printing ──────────────────────────────────────── */
  const fetchShipmentForPrint = async (id) => {
    setIsPrintLoading(true);
    try {
      const { data } = await api.get(`/api/user/shipment/${id}`);
      setPrintShipment(data.data || data);
      setIsPrintModalOpen(true);
    } catch (err) {
      setNotice({
        title: "Print unavailable",
        message: "Failed to load shipment details for printing.",
      });
    } finally {
      setIsPrintLoading(false);
    }
  };

  /* ── Handlers ─────────────────────────────────────────────────────────── */
  const handleLogout = async () => {
    await logout();
    navigate("/application-user-login");
  };

  const handleDelete = (id) => setDeleteTargetId(id);

  const confirmDelete = async () => {
    const id = deleteTargetId;
    setIsDeleting(true);
    try {
      await api.delete(`/api/user/shipment/${id}`);
      setShipments((prev) => prev.filter((s) => s.id !== id && s._id !== id));
      setDeleteTargetId(null);
    } catch (err) {
      setDeleteTargetId(null);
      setNotice({
        title: "Delete failed",
        message:
          err?.response?.data?.message ||
          "Could not delete the shipment. Please try again.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /* ── Route control: pause & reroute / resume ────────────────────────────── */
  const canManageRoute = (shipment) => {
    if (!currentUser || !shipment) return false;
    if (currentUser.role === "admin") return true;
    const creatorId =
      typeof shipment.createdBy === "object"
        ? shipment.createdBy?._id
        : shipment.createdBy;
    return creatorId && creatorId.toString() === currentUser._id?.toString();
  };

  const openPauseForm = () => {
    setRouteActionError("");
    setRouteAddress({
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    });
    setRouteReason("");
    setShowPauseForm(true);
  };

  const handlePauseSubmit = async (e) => {
    e.preventDefault();
    if (!selectedShipment) return;
    const id = getShipmentId(selectedShipment);
    setRouteActionLoading(true);
    setRouteActionError("");
    try {
      await api.patch(`/api/user/shipment/${id}/pause`, {
        address: routeAddress,
        reason: routeReason,
      });
      setShowPauseForm(false);
      await fetchShipmentDetails(id);
      fetchShipments();
    } catch (err) {
      setRouteActionError(
        err?.response?.data?.message || "Failed to pause shipment.",
      );
    } finally {
      setRouteActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!selectedShipment) return;
    const id = getShipmentId(selectedShipment);
    setRouteActionLoading(true);
    setRouteActionError("");
    try {
      await api.patch(`/api/user/shipment/${id}/resume`, {
        note: resumeNote,
      });
      setResumeNote("");
      await fetchShipmentDetails(id);
      fetchShipments();
    } catch (err) {
      setRouteActionError(
        err?.response?.data?.message || "Failed to resume shipment.",
      );
    } finally {
      setRouteActionLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedShipment(null);
    setModalError(null);
  };

  const closePrintModal = () => {
    setIsPrintModalOpen(false);
    setPrintShipment(null);
  };

  const handlePrint = () => {
    const s = printShipment;
    const trackingNum = s.trackingNumber || "—";
    const statusColor = STATUS_META[s.status]?.color || "#8E8E93";
    const totalAmt = getTotalPrice(s);

    const historyRows =
      s.trackingHistory && s.trackingHistory.length > 0
        ? s.trackingHistory
            .slice()
            .reverse()
            .map((ev) => {
              const isHeld = ev.eventType === "pause";
              const isReleased = ev.eventType === "resume";
              const dotColor = isHeld
                ? "#e53e3e"
                : isReleased
                  ? "#38a169"
                  : "#d4af37";
              const addr = ev.address
                ? `${ev.address.street ? `${ev.address.street}, ` : ""}${ev.address.city}, ${ev.address.state} ${ev.address.postalCode}, ${ev.address.country}`
                : "";
              return `
          <div class="history-row">
            <div class="history-dot" style="background:${dotColor}"></div>
            <div class="history-info">
              <span class="history-status"${isHeld ? ' style="color:#e53e3e"' : ""}>${isHeld ? "HELD" : ev.status}</span>
              <span class="history-desc">${ev.description || ""}</span>
              ${addr ? `<span class="history-addr${isHeld ? " held" : ""}">${addr}</span>` : ""}
              <span class="history-date">${ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ""}</span>
            </div>
          </div>`;
            })
            .join("")
        : "<p style='color:#aaa;font-size:11px;margin:4px 0'>No tracking history available.</p>";

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Shipping Slip — ${trackingNum}</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      background: #fff;
      color: #1a1a2e;
      padding: 24px;
      max-width: 780px;
      margin: 0 auto;
    }

    /* ── Header ── */
    .slip-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 12px;
      border-bottom: 3px solid #0b1e33;
      margin-bottom: 14px;
    }
    .company-block { flex: 1; }
    .company-name {
      font-size: 20px;
      font-weight: 900;
      color: #0b1e33;
      letter-spacing: 1px;
      line-height: 1.1;
    }
    .company-sub {
      font-size: 10px;
      color: #888;
      margin-top: 2px;
      letter-spacing: 0.5px;
    }
    .slip-label {
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      background: #0b1e33;
      padding: 4px 14px;
      border-radius: 4px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* ── Status + Service bar ── */
    .info-bar {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
      flex-wrap: wrap;
    }
    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;
      color: ${statusColor};
      border: 1.5px solid ${statusColor};
      background: ${STATUS_META[s.status]?.bg || "rgba(142,142,147,0.12)"};
    }
    .status-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: ${statusColor};
      display: inline-block;
    }
    .meta-chip {
      font-size: 10px;
      font-weight: 600;
      color: #555;
      background: #f3f3f3;
      padding: 3px 10px;
      border-radius: 12px;
    }
    .meta-chip.gold {
      background: #fff8e1;
      color: #b8860b;
    }

    /* ── Barcode row ── */
    .barcode-row {
      text-align: center;
      margin: 10px 0 16px;
      padding: 10px 0;
      border-top: 1px dashed #ddd;
      border-bottom: 1px dashed #ddd;
    }
    .barcode-row svg { display: block; margin: 0 auto; }
    .tracking-text {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 2px;
      color: #0b1e33;
      margin-top: 6px;
      font-family: 'Courier New', monospace;
    }

    /* ── Address grid ── */
    .address-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 14px;
    }
    .address-box {
      border: 2px solid #0b1e33;
      border-radius: 8px;
      padding: 12px 14px;
      position: relative;
    }
    .address-box.dest {
      border-color: #d4af37;
      border-width: 2.5px;
    }
    .addr-label {
      font-size: 9px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #fff;
      background: #0b1e33;
      padding: 2px 8px;
      border-radius: 3px;
      display: inline-block;
      margin-bottom: 8px;
    }
    .address-box.dest .addr-label {
      background: #d4af37;
      color: #0b1e33;
    }
    .addr-name {
      font-size: 14px;
      font-weight: 800;
      color: #0b1e33;
      line-height: 1.2;
    }
    .addr-company {
      font-size: 11px;
      color: #555;
      margin: 2px 0;
      font-style: italic;
    }
    .addr-street {
      font-size: 12px;
      color: #333;
      margin: 5px 0 2px;
      line-height: 1.4;
    }
    .addr-city {
      font-size: 13px;
      font-weight: 700;
      color: #0b1e33;
    }
    .addr-contact {
      font-size: 10px;
      color: #777;
      margin-top: 6px;
      line-height: 1.5;
    }

    /* ── Details grid ── */
    .details-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 14px;
      background: #f8f9fa;
      padding: 10px 12px;
      border-radius: 8px;
    }
    .detail-cell { }
    .detail-lbl {
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #999;
      margin-bottom: 2px;
    }
    .detail-val {
      font-size: 12px;
      font-weight: 600;
      color: #0b1e33;
    }

    /* ── Pricing ── */
    .pricing-row {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
      align-items: center;
      margin-bottom: 14px;
      padding: 10px 14px;
      background: #0b1e33;
      border-radius: 8px;
      color: #fff;
    }
    .price-item { text-align: right; }
    .price-lbl { font-size: 9px; color: #aaa; text-transform: uppercase; letter-spacing: 0.8px; }
    .price-val { font-size: 13px; font-weight: 700; }
    .price-total .price-val { color: #d4af37; font-size: 16px; }

    /* ── Special Instructions ── */
    .instructions-box {
      background: #fff8e1;
      border-left: 3px solid #d4af37;
      padding: 8px 12px;
      border-radius: 0 6px 6px 0;
      font-size: 11px;
      color: #7a5c00;
      margin-bottom: 14px;
    }
    .instructions-box strong { display: block; margin-bottom: 3px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }

    /* ── Tracking History ── */
    .history-section { margin-bottom: 14px; }
    .section-title {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #0b1e33;
      border-bottom: 1.5px solid #e2e8f0;
      padding-bottom: 4px;
      margin-bottom: 8px;
    }
    .history-row {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin-bottom: 7px;
    }
    .history-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #d4af37;
      margin-top: 3px;
      flex-shrink: 0;
    }
    .history-info { display: flex; flex-direction: column; }
    .history-status { font-size: 11px; font-weight: 700; color: #0b1e33; }
    .history-desc { font-size: 10px; color: #666; }
    .history-addr {
      font-size: 10px; color: #0b1e33; background: #f4f6f9;
      border-left: 3px solid #d4af37; border-radius: 0 4px 4px 0;
      padding: 3px 7px; margin: 3px 0; display: inline-block;
      align-self: flex-start;
    }
    .history-addr.held { background: #fff5f5; border-left-color: #e53e3e; }
    .history-date { font-size: 9px; color: #aaa; margin-top: 1px; }

    /* ── Held banner ── */
    .held-banner {
      background: linear-gradient(135deg, #c53030, #9b2c2c); color: #fff;
      text-align: center; font-weight: 800; letter-spacing: 2px;
      font-size: 12px; text-transform: uppercase;
      padding: 8px; border-radius: 6px; margin-bottom: 8px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .held-at-line {
      background: #fff5f5; border: 1.5px solid #e53e3e; border-radius: 6px;
      padding: 6px 10px; margin-bottom: 12px; font-size: 10.5px; color: #0b1e33;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .held-at-line b {
      color: #e53e3e; text-transform: uppercase; font-size: 8.5px;
      letter-spacing: 1px; display: block; margin-bottom: 1px;
    }

    /* ── Bottom barcode ── */
    .bottom-barcode {
      text-align: center;
      margin: 10px 0 8px;
      padding-top: 10px;
      border-top: 1px dashed #ddd;
    }
    .bottom-barcode svg { display: block; margin: 0 auto; }

    /* ── Footer ── */
    .slip-footer {
      text-align: center;
      font-size: 9px;
      color: #bbb;
      padding-top: 10px;
      border-top: 1px solid #eee;
      line-height: 1.6;
    }
    .slip-footer strong { color: #888; }

    /* ── Signature ── */
    .signature-row {
      display: flex;
      gap: 20px;
      margin-bottom: 10px;
    }
    .sig-box {
      flex: 1;
      border-top: 1.5px solid #ccc;
      padding-top: 4px;
      font-size: 9px;
      color: #aaa;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    @media print {
      body { padding: 12px; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <!-- HEADER -->
  <div class="slip-header">
    <div class="company-block">
      <div class="company-name">TRANS WORLD SHIPPING CO.</div>
      <div class="company-sub">3748 Bayer Avenue, Suite 201 · Long Beach, CA 90808 · +1-800-555-SHIP</div>
    </div>
    <div class="slip-label">Shipping Slip</div>
  </div>

  <!-- STATUS BAR -->
  <div class="info-bar">
    <span class="status-pill"><span class="status-dot"></span>${s.status || "Unknown"}</span>
    <span class="meta-chip gold">${s.serviceType || "Standard"}</span>
    <span class="meta-chip">Carrier: ${s.carrier || "Trans World Shipping"}</span>
    ${s.isFragile ? '<span class="meta-chip" style="background:#fff0f0;color:#e53e3e;">⚠ FRAGILE</span>' : ""}
    ${s.requiresSignature ? '<span class="meta-chip" style="background:#e8f5e9;color:#2e7d32;">✎ Signature Required</span>' : ""}
  </div>

  ${
    s.isPaused
      ? `<div class="held-banner">Shipment currently held</div>
  ${
    s.currentAddress
      ? `<div class="held-at-line"><b>Held at</b>${s.currentAddress.street ? `${s.currentAddress.street}, ` : ""}${s.currentAddress.city}, ${s.currentAddress.state} ${s.currentAddress.postalCode}, ${s.currentAddress.country}</div>`
      : ""
  }`
      : ""
  }

  <!-- PRIMARY BARCODE -->
  <div class="barcode-row">
    <svg id="barcode-main"></svg>
    <div class="tracking-text">${trackingNum}</div>
  </div>

  <!-- ADDRESSES -->
  <div class="address-grid">
    <div class="address-box">
      <span class="addr-label">From</span>
      <div class="addr-name">${s.sender?.name || "—"}</div>
      ${s.sender?.company ? `<div class="addr-company">${s.sender.company}</div>` : ""}
      <div class="addr-street">
        ${s.originAddress?.street || ""}<br/>
        ${s.originAddress?.city || ""}, ${s.originAddress?.state || ""} ${s.originAddress?.postalCode || ""}
      </div>
      <div class="addr-city">${s.originAddress?.country || ""}</div>
      <div class="addr-contact">
        ${s.sender?.email ? `✉ ${s.sender.email}` : ""}<br/>
        ${s.sender?.phone ? `✆ ${s.sender.phone}` : ""}
      </div>
    </div>
    <div class="address-box dest">
      <span class="addr-label">To</span>
      <div class="addr-name">${s.receiver?.name || "—"}</div>
      ${s.receiver?.company ? `<div class="addr-company">${s.receiver.company}</div>` : ""}
      <div class="addr-street">
        ${s.destinationAddress?.street || ""}<br/>
        ${s.destinationAddress?.city || ""}, ${s.destinationAddress?.state || ""} ${s.destinationAddress?.postalCode || ""}
      </div>
      <div class="addr-city">${s.destinationAddress?.country || ""}</div>
      <div class="addr-contact">
        ${s.receiver?.email ? `✉ ${s.receiver.email}` : ""}<br/>
        ${s.receiver?.phone ? `✆ ${s.receiver.phone}` : ""}
      </div>
    </div>
  </div>

  <!-- PACKAGE DETAILS (only fields that were actually filled in) -->
  <div class="details-grid">
    <div class="detail-cell">
      <div class="detail-lbl">Weight</div>
      <div class="detail-val">${s.weight?.value || "—"} ${s.weight?.unit || ""}</div>
    </div>
    ${
      s.dimensions?.length || s.dimensions?.width || s.dimensions?.height
        ? `<div class="detail-cell">
      <div class="detail-lbl">Dimensions</div>
      <div class="detail-val">${s.dimensions?.length || "—"} × ${s.dimensions?.width || "—"} × ${s.dimensions?.height || "—"} ${s.dimensions?.unit || ""}</div>
    </div>`
        : ""
    }
    ${
      s.description
        ? `<div class="detail-cell">
      <div class="detail-lbl">Description</div>
      <div class="detail-val">${s.description}</div>
    </div>`
        : ""
    }
    <div class="detail-cell">
      <div class="detail-lbl">Sent Date</div>
      <div class="detail-val">${s.sentDate ? new Date(s.sentDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</div>
    </div>
    <div class="detail-cell">
      <div class="detail-lbl">Est. Delivery</div>
      <div class="detail-val">${s.estimatedDeliveryDate ? new Date(s.estimatedDeliveryDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</div>
    </div>
    <div class="detail-cell">
      <div class="detail-lbl">Transit Days</div>
      <div class="detail-val">${s.estimatedDaysInTransit ? s.estimatedDaysInTransit + " days" : "—"}</div>
    </div>
  </div>

  <!-- PRICING (only charges that were actually filled in) -->
  ${
    s.shippingCost || s.insurance
      ? `<div class="pricing-row">
    ${
      s.shippingCost
        ? `<div class="price-item">
      <div class="price-lbl">Shipping Cost</div>
      <div class="price-val">$${s.shippingCost.toLocaleString()}</div>
    </div>`
        : ""
    }
    ${
      s.insurance
        ? `<div class="price-item">
      <div class="price-lbl">Insurance</div>
      <div class="price-val">$${s.insurance.toLocaleString()}</div>
    </div>`
        : ""
    }
    <div class="price-item price-total">
      <div class="price-lbl">Total Amount</div>
      <div class="price-val">$${totalAmt.toLocaleString()}</div>
    </div>
  </div>`
      : ""
  }

  ${s.specialInstructions ? `
  <div class="instructions-box">
    <strong>Special Instructions</strong>
    ${s.specialInstructions}
  </div>` : ""}

  <!-- TRACKING HISTORY -->
  ${s.trackingHistory && s.trackingHistory.length > 0 ? `
  <div class="history-section">
    <div class="section-title">Tracking History</div>
    ${historyRows}
  </div>` : ""}

  <!-- SIGNATURE LINES -->
  <div class="signature-row">
    <div class="sig-box">Sender Signature</div>
    <div class="sig-box">Receiver Signature</div>
    <div class="sig-box">Date Received</div>
  </div>

  <!-- BOTTOM BARCODE -->
  <div class="bottom-barcode">
    <svg id="barcode-bottom"></svg>
  </div>

  <!-- FOOTER -->
  <div class="slip-footer">
    <strong>Trans World Shipping Company</strong> · info@transworldshippingcompany.com · www.transworldshipping.delivery<br/>
    Generated: ${new Date().toLocaleString()} · This document serves as proof of shipment.
  </div>

  <script>
    window.onload = function() {
      var opts = {
        format: "CODE128",
        width: 2,
        height: 50,
        displayValue: false,
        margin: 4,
        background: "#ffffff",
        lineColor: "#0b1e33"
      };
      try {
        JsBarcode("#barcode-main", "${trackingNum}", { ...opts, height: 60, width: 2 });
        JsBarcode("#barcode-bottom", "${trackingNum}", { ...opts, height: 40, width: 1.5 });
      } catch(e) {
        document.getElementById("barcode-main").style.display = "none";
        document.getElementById("barcode-bottom").style.display = "none";
      }
      setTimeout(function() {
        window.print();
        window.onafterprint = function() { window.close(); };
      }, 600);
    };
  <\/script>
</body>
</html>`);
    printWindow.document.close();
  };

  // Get sender name from the sender object
  const getSenderName = (shipment) => {
    if (shipment.sender?.name) return shipment.sender.name;
    if (shipment.senderName) return shipment.senderName;
    if (typeof shipment.sender === "string") return shipment.sender;
    return "—";
  };

  // Get tracking number
  const getTrackingNumber = (shipment) => {
    return shipment.trackingNumber || shipment.tracking || "—";
  };

  // Get shipment ID
  const getShipmentId = (shipment) => {
    return shipment.id || shipment._id;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Calculate total price
  const getTotalPrice = (shipment) => {
    const shippingCost = shipment.shippingCost || 0;
    const insurance = shipment.insurance || 0;
    return shippingCost + insurance;
  };

  const filtered = shipments.filter((s) => {
    const senderName = getSenderName(s).toLowerCase();
    const trackingNum = getTrackingNumber(s).toLowerCase();
    const searchTerm = search.toLowerCase();

    const matchSearch =
      senderName.includes(searchTerm) || trackingNum.includes(searchTerm);
    const matchStatus = filterStatus === "All" || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <Helmet>
        <title>Shipments | Admin Dashboard</title>
      </Helmet>

      <Shell>
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <Sidebar className={sidebarOpen ? "open" : "closed"}>
          <div className="sb-header">
            <div className="brand">
              <FaShip className="brand-icon" />
              {sidebarOpen && <span>TWS Admin</span>}
            </div>
            <button
              className="toggle-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          <nav className="sb-nav">
            <button className="nav-item active">
              <FaShip />
              {sidebarOpen && <span>Shipments</span>}
            </button>
            {currentUser?.role === "admin" && (
              <button
                className="nav-item"
                onClick={() => navigate("/admin/managers")}
              >
                <FaUsersCog />
                {sidebarOpen && <span>App Managers</span>}
              </button>
            )}
          </nav>

          <div className="sb-footer">
            <button className="nav-item logout" onClick={handleLogout}>
              <FaSignOutAlt />
              {sidebarOpen && <span>Logout</span>}
            </button>
          </div>
        </Sidebar>

        {/* ── Main ────────────────────────────────────────────────────── */}
        <Main className={sidebarOpen ? "" : "expanded"}>
          {/* Top bar */}
          <TopBar>
            <div className="title-block">
              <h1>Shipments</h1>
              <p>{filtered.length} total records</p>
            </div>

            <CreateBtn onClick={() => navigate("/admin/shipments/create")}>
              <FaPlus /> Create Shipment
            </CreateBtn>
          </TopBar>

          {/* Toolbar */}
          <Toolbar>
            <SearchBox>
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by sender or tracking number…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </SearchBox>

            <FilterDropdown>
              <button
                className="filter-trigger"
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <FaFilter />
                <span>{filterStatus}</span>
                <FaChevronDown className={filterOpen ? "rotated" : ""} />
              </button>
              {filterOpen && (
                <ul className="filter-menu">
                  {[
                    "All",
                    "Packaging",
                    "Shipped",
                    "In Transit",
                    "Out for Delivery",
                    "Delivered",
                    "Delayed",
                    "Cancelled",
                  ].map((s) => (
                    <li
                      key={s}
                      className={filterStatus === s ? "selected" : ""}
                      onClick={() => {
                        setFilterStatus(s);
                        setFilterOpen(false);
                        setPage(1);
                      }}
                    >
                      {s !== "All" && (
                        <span
                          className="dot"
                          style={{ background: STATUS_META[s]?.color }}
                        />
                      )}
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </FilterDropdown>
          </Toolbar>

          {/* Table */}
          <TableCard>
            {isLoading ? (
              <SpinnerWrap>
                <div className="spinner" />
                <p>Loading shipments…</p>
              </SpinnerWrap>
            ) : error ? (
              <ErrorWrap>
                <FaExclamationTriangle className="err-icon" />
                <p>{error}</p>
                <button onClick={fetchShipments}>
                  <FaRedo /> Retry
                </button>
              </ErrorWrap>
            ) : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Tracking No.</th>
                        <th>Sender Name</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="empty-row">
                            No shipments found.
                          </td>
                        </tr>
                      ) : (
                        paginated.map((s, idx) => (
                          <tr key={getShipmentId(s)}>
                            <td className="row-num">
                              {(page - 1) * PAGE_SIZE + idx + 1}
                            </td>
                            <td className="tracking">{getTrackingNumber(s)}</td>
                            <td className="sender">{getSenderName(s)}</td>
                            <td>
                              <StatusBadge
                                color={
                                  STATUS_META[s.status]?.color ?? "#8E8E93"
                                }
                                bg={
                                  STATUS_META[s.status]?.bg ??
                                  "rgba(142,142,147,0.12)"
                                }
                              >
                                <span className="dot" />
                                {s.status ?? "Unknown"}
                              </StatusBadge>
                            </td>
                            <td>
                              <ActionGroup>
                                <ActionBtn
                                  title="View"
                                  aria-label="View shipment"
                                  $variant="view"
                                  onClick={() =>
                                    fetchShipmentDetails(getShipmentId(s))
                                  }
                                >
                                  <FaEye />
                                </ActionBtn>
                                <ActionBtn
                                  title="Print Invoice"
                                  aria-label="Print shipment"
                                  $variant="view"
                                  onClick={() =>
                                    fetchShipmentForPrint(getShipmentId(s))
                                  }
                                >
                                  <FaPrint />
                                </ActionBtn>
                                <ActionBtn
                                  title="Edit"
                                  aria-label="Edit shipment"
                                  $variant="edit"
                                  onClick={() =>
                                    navigate(
                                      `/admin/shipments/${getShipmentId(s)}/edit`,
                                    )
                                  }
                                >
                                  <FaEdit />
                                </ActionBtn>
                                <ActionBtn
                                  title="Delete"
                                  aria-label="Delete shipment"
                                  $variant="delete"
                                  onClick={() => handleDelete(getShipmentId(s))}
                                >
                                  <FaTrash />
                                </ActionBtn>
                              </ActionGroup>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <Pagination>
                    <button
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <FaChevronLeft />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (n) => (
                        <button
                          key={n}
                          className={n === page ? "active" : ""}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      ),
                    )}
                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <FaChevronRight />
                    </button>
                  </Pagination>
                )}
              </>
            )}
          </TableCard>
        </Main>
      </Shell>

      {/* Modal for Shipment Details */}
      {isModalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Shipment Details</h2>
              <CloseButton onClick={closeModal}>&times;</CloseButton>
            </ModalHeader>

            {isModalLoading ? (
              <ModalSpinner>
                <div className="spinner" />
                <p>Loading details...</p>
              </ModalSpinner>
            ) : modalError ? (
              <ModalError>
                <FaExclamationTriangle />
                <p>{modalError}</p>
                <button
                  onClick={() => fetchShipmentDetails(selectedShipment?._id)}
                >
                  Retry
                </button>
              </ModalError>
            ) : (
              selectedShipment && (
                <ModalBody>
                  <DetailSection>
                    <SectionTitle>
                      <FaInfoCircle /> Tracking Information
                    </SectionTitle>
                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Tracking Number</DetailLabel>
                        <DetailValue>
                          {selectedShipment.trackingNumber || "—"}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Status</DetailLabel>
                        <StatusBadgeSmall $status={selectedShipment.status}>
                          {selectedShipment.status || "Unknown"}
                        </StatusBadgeSmall>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Carrier</DetailLabel>
                        <DetailValue>
                          {selectedShipment.carrier || "—"}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Service Type</DetailLabel>
                        <DetailValue>
                          {selectedShipment.serviceType || "—"}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>
                  </DetailSection>

                  {canManageRoute(selectedShipment) && (
                    <DetailSection>
                      <SectionTitle>
                        {selectedShipment.isPaused ? (
                          <FaPauseCircle />
                        ) : (
                          <FaPlayCircle />
                        )}{" "}
                        Route Control
                      </SectionTitle>

                      {selectedShipment.isPaused ? (
                        <RouteControlBox>
                          <p className="route-status paused">
                            <FaPauseCircle /> Shipment is currently paused
                          </p>
                          {selectedShipment.currentAddress && (
                            <AddressText>
                              {selectedShipment.currentAddress.street}
                              <br />
                              {selectedShipment.currentAddress.city},{" "}
                              {selectedShipment.currentAddress.state}{" "}
                              {selectedShipment.currentAddress.postalCode}
                              <br />
                              {selectedShipment.currentAddress.country}
                            </AddressText>
                          )}
                          <RouteInput
                            type="text"
                            placeholder="Optional note (e.g. cleared customs, departing facility)"
                            value={resumeNote}
                            onChange={(e) => setResumeNote(e.target.value)}
                          />
                          {routeActionError && (
                            <p className="route-error">{routeActionError}</p>
                          )}
                          <RouteActionBtn
                            type="button"
                            onClick={handleResume}
                            disabled={routeActionLoading}
                          >
                            <FaPlayCircle />{" "}
                            {routeActionLoading
                              ? "Resuming…"
                              : "Resume Shipment"}
                          </RouteActionBtn>
                        </RouteControlBox>
                      ) : !showPauseForm ? (
                        <RouteControlBox>
                          <RouteActionBtn
                            type="button"
                            $variant="pause"
                            onClick={openPauseForm}
                          >
                            <FaPauseCircle /> Pause &amp; Reroute
                          </RouteActionBtn>
                        </RouteControlBox>
                      ) : (
                        <RouteControlBox as="form" onSubmit={handlePauseSubmit}>
                          <RouteFormGrid>
                            <input
                              type="text"
                              placeholder="Street *"
                              value={routeAddress.street}
                              onChange={(e) =>
                                setRouteAddress((p) => ({
                                  ...p,
                                  street: e.target.value,
                                }))
                              }
                              required
                            />
                            <input
                              type="text"
                              placeholder="City *"
                              value={routeAddress.city}
                              onChange={(e) =>
                                setRouteAddress((p) => ({
                                  ...p,
                                  city: e.target.value,
                                }))
                              }
                              required
                            />
                            <input
                              type="text"
                              placeholder="State *"
                              value={routeAddress.state}
                              onChange={(e) =>
                                setRouteAddress((p) => ({
                                  ...p,
                                  state: e.target.value,
                                }))
                              }
                              required
                            />
                            <input
                              type="text"
                              placeholder="Postal Code *"
                              value={routeAddress.postalCode}
                              onChange={(e) =>
                                setRouteAddress((p) => ({
                                  ...p,
                                  postalCode: e.target.value,
                                }))
                              }
                              required
                            />
                            <input
                              type="text"
                              placeholder="Country *"
                              value={routeAddress.country}
                              onChange={(e) =>
                                setRouteAddress((p) => ({
                                  ...p,
                                  country: e.target.value,
                                }))
                              }
                              required
                            />
                          </RouteFormGrid>
                          <RouteInput
                            type="text"
                            placeholder="Reason (optional, e.g. weather delay, awaiting customer instruction)"
                            value={routeReason}
                            onChange={(e) => setRouteReason(e.target.value)}
                          />
                          {routeActionError && (
                            <p className="route-error">{routeActionError}</p>
                          )}
                          <RouteFormActions>
                            <CancelRouteBtn
                              type="button"
                              onClick={() => setShowPauseForm(false)}
                            >
                              Cancel
                            </CancelRouteBtn>
                            <RouteActionBtn
                              type="submit"
                              $variant="pause"
                              disabled={routeActionLoading}
                            >
                              <FaPauseCircle />{" "}
                              {routeActionLoading
                                ? "Pausing…"
                                : "Confirm Pause & Reroute"}
                            </RouteActionBtn>
                          </RouteFormActions>
                        </RouteControlBox>
                      )}
                    </DetailSection>
                  )}

                  <DetailSection>
                    <SectionTitle>
                      <FaMapMarkerAlt /> Addresses
                    </SectionTitle>
                    <AddressGrid>
                      <AddressBox>
                        <AddressTitle>Origin</AddressTitle>
                        <AddressText>
                          {selectedShipment.originAddress?.street}
                          <br />
                          {selectedShipment.originAddress?.city},{" "}
                          {selectedShipment.originAddress?.state}{" "}
                          {selectedShipment.originAddress?.postalCode}
                          <br />
                          {selectedShipment.originAddress?.country}
                        </AddressText>
                      </AddressBox>
                      <AddressBox>
                        <AddressTitle>Destination</AddressTitle>
                        <AddressText>
                          {selectedShipment.destinationAddress?.street}
                          <br />
                          {selectedShipment.destinationAddress?.city},{" "}
                          {selectedShipment.destinationAddress?.state}{" "}
                          {selectedShipment.destinationAddress?.postalCode}
                          <br />
                          {selectedShipment.destinationAddress?.country}
                        </AddressText>
                      </AddressBox>
                    </AddressGrid>
                  </DetailSection>

                  <DetailSection>
                    <SectionTitle>
                      <FaUser /> Parties
                    </SectionTitle>
                    <PartiesGrid>
                      <PartyBox>
                        <PartyTitle>Shipped By</PartyTitle>
                        <PartyDetail>
                          <strong>{selectedShipment.sender?.name}</strong>
                        </PartyDetail>
                        {selectedShipment.sender?.company && (
                          <PartyDetail>
                            {selectedShipment.sender.company}
                          </PartyDetail>
                        )}
                        <PartyDetail>
                          <FaEnvelope /> {selectedShipment.sender?.email}
                        </PartyDetail>
                        <PartyDetail>
                          <FaPhone /> {selectedShipment.sender?.phone}
                        </PartyDetail>
                      </PartyBox>
                      <PartyBox>
                        <PartyTitle>Shipping To</PartyTitle>
                        <PartyDetail>
                          <strong>{selectedShipment.receiver?.name}</strong>
                        </PartyDetail>
                        {selectedShipment.receiver?.company && (
                          <PartyDetail>
                            {selectedShipment.receiver.company}
                          </PartyDetail>
                        )}
                        <PartyDetail>
                          <FaEnvelope /> {selectedShipment.receiver?.email}
                        </PartyDetail>
                        <PartyDetail>
                          <FaPhone /> {selectedShipment.receiver?.phone}
                        </PartyDetail>
                      </PartyBox>
                    </PartiesGrid>
                  </DetailSection>

                  <DetailSection>
                    <SectionTitle>
                      <FaBox /> Package Details
                    </SectionTitle>
                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Weight</DetailLabel>
                        <DetailValue>
                          {selectedShipment.weight?.value}{" "}
                          {selectedShipment.weight?.unit}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Dimensions</DetailLabel>
                        <DetailValue>
                          {selectedShipment.dimensions?.length} ×{" "}
                          {selectedShipment.dimensions?.width} ×{" "}
                          {selectedShipment.dimensions?.height}{" "}
                          {selectedShipment.dimensions?.unit}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Description</DetailLabel>
                        <DetailValue>
                          {selectedShipment.description || "—"}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Fragile</DetailLabel>
                        <DetailValue>
                          {selectedShipment.isFragile ? "Yes" : "No"}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>
                  </DetailSection>

                  <DetailSection>
                    <SectionTitle>
                      <FaCalendarAlt /> Dates
                    </SectionTitle>
                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Sent Date</DetailLabel>
                        <DetailValue>
                          {formatDate(selectedShipment.sentDate)}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Est. Delivery</DetailLabel>
                        <DetailValue>
                          {formatDate(selectedShipment.estimatedDeliveryDate)}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Est. Days in Transit</DetailLabel>
                        <DetailValue>
                          {selectedShipment.estimatedDaysInTransit} days
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Actual Delivery</DetailLabel>
                        <DetailValue>
                          {formatDate(selectedShipment.actualDeliveryDate)}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>
                  </DetailSection>

                  <DetailSection>
                    <SectionTitle>
                      <FaMoneyBillWave /> Pricing
                    </SectionTitle>
                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Shipping Cost</DetailLabel>
                        <DetailValue>
                          $
                          {selectedShipment.shippingCost?.toLocaleString() || 0}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Insurance</DetailLabel>
                        <DetailValue>
                          ${selectedShipment.insurance?.toLocaleString() || 0}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>
                  </DetailSection>

                  {selectedShipment.specialInstructions && (
                    <DetailSection>
                      <SectionTitle>
                        <FaShieldAlt /> Special Instructions
                      </SectionTitle>
                      <SpecialInstructions>
                        {selectedShipment.specialInstructions}
                      </SpecialInstructions>
                    </DetailSection>
                  )}

                  {selectedShipment.trackingHistory &&
                    selectedShipment.trackingHistory.length > 0 && (
                      <DetailSection>
                        <SectionTitle>
                          <FaTruck /> Tracking History
                        </SectionTitle>
                        <TrackingTimeline>
                          {[...selectedShipment.trackingHistory]
                            .reverse()
                            .map((event, idx, arr) => {
                              const isStopover =
                                event.eventType === "pause" ||
                                event.eventType === "resume";
                              return (
                                <TimelineItem key={idx} $isFirst={idx === 0} $isLast={idx === arr.length - 1}>
                                  <TimelineLeft>
                                    <TimelineDot
                                      $isFirst={idx === 0}
                                      $status={event.status}
                                      $stopover={isStopover}
                                    />
                                    {idx < arr.length - 1 && <TimelineConnector />}
                                  </TimelineLeft>
                                  <TimelineCard $isFirst={idx === 0} $stopover={isStopover}>
                                    <TimelineStatus $isFirst={idx === 0}>
                                      {event.eventType === "pause" && (
                                        <FaPauseCircle className="stopover-icon" />
                                      )}
                                      {event.eventType === "resume" && (
                                        <FaPlayCircle className="stopover-icon" />
                                      )}
                                      {event.status}
                                    </TimelineStatus>
                                    {event.description && (
                                      <TimelineDesc>{event.description}</TimelineDesc>
                                    )}
                                    {event.address && (
                                      <TimelineAddress>
                                        {event.address.street && (
                                          <>
                                            {event.address.street}
                                            <br />
                                          </>
                                        )}
                                        {event.address.city}, {event.address.state}{" "}
                                        {event.address.postalCode}
                                        <br />
                                        {event.address.country}
                                      </TimelineAddress>
                                    )}
                                    <TimelineDate>
                                      {event.timestamp
                                        ? new Date(event.timestamp).toLocaleString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "—"}
                                    </TimelineDate>
                                  </TimelineCard>
                                </TimelineItem>
                              );
                            })}
                        </TrackingTimeline>
                      </DetailSection>
                    )}
                </ModalBody>
              )
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Print Modal */}
      {isPrintModalOpen && printShipment && (
        <ModalOverlay onClick={closePrintModal}>
          <PrintModalContent onClick={(e) => e.stopPropagation()}>
            {isPrintLoading ? (
              <ModalSpinner>
                <div className="spinner" />
                <p>Loading invoice...</p>
              </ModalSpinner>
            ) : (
              <>
                {/* Brand band */}
                <InvoiceBrandHeader>
                  <div className="brand">
                    <FaShip className="brand-icon" />
                    <div>
                      <h2>Trans World Shipping Co.</h2>
                      <span>
                        Global Reach · Local Expertise · Reliable Delivery
                      </span>
                    </div>
                  </div>
                  <div className="header-right">
                    <span className="invoice-badge">Invoice</span>
                    <button
                      className="close-x"
                      onClick={closePrintModal}
                      aria-label="Close"
                    >
                      &times;
                    </button>
                  </div>
                </InvoiceBrandHeader>

                {/* Meta bar */}
                <InvoiceMetaBar>
                  <div className="meta">
                    <span className="meta-label">Tracking No.</span>
                    <span className="meta-value mono">
                      {printShipment.trackingNumber || "—"}
                    </span>
                  </div>
                  <div className="meta">
                    <span className="meta-label">Invoice Date</span>
                    <span className="meta-value">
                      {formatDate(new Date())}
                    </span>
                  </div>
                  <div className="meta">
                    <span className="meta-label">Service</span>
                    <span className="meta-value">
                      {printShipment.serviceType || "Standard"}
                    </span>
                  </div>
                  <div className="meta">
                    <span className="meta-label">Status</span>
                    <StatusBadgeSmall $status={printShipment.status}>
                      {printShipment.status || "Unknown"}
                    </StatusBadgeSmall>
                  </div>
                </InvoiceMetaBar>

                <PrintModalBody>
                  {printShipment.isPaused && (
                    <InvoiceHeldStrip>
                      Shipment currently held
                      {printShipment.currentAddress
                        ? ` — ${printShipment.currentAddress.city}, ${printShipment.currentAddress.state}`
                        : ""}
                    </InvoiceHeldStrip>
                  )}

                  {/* From / To */}
                  <InvoicePartyGrid>
                    <InvoicePartyCard>
                      <span className="party-tag">From</span>
                      <div className="party-name">
                        {printShipment.sender?.company ||
                          printShipment.sender?.name}
                      </div>
                      {printShipment.sender?.company && (
                        <div className="party-line">
                          Contact: {printShipment.sender.name}
                        </div>
                      )}
                      <div className="party-line">
                        {printShipment.sender?.email}
                      </div>
                      <div className="party-line">
                        {printShipment.sender?.phone}
                      </div>
                      <div className="party-line address">
                        {printShipment.originAddress?.street}
                        <br />
                        {printShipment.originAddress?.city},{" "}
                        {printShipment.originAddress?.state}{" "}
                        {printShipment.originAddress?.postalCode}
                        <br />
                        {printShipment.originAddress?.country}
                      </div>
                    </InvoicePartyCard>

                    <InvoicePartyCard className="to">
                      <span className="party-tag">To</span>
                      <div className="party-name">
                        {printShipment.receiver?.company ||
                          printShipment.receiver?.name}
                      </div>
                      {printShipment.receiver?.company && (
                        <div className="party-line">
                          Contact: {printShipment.receiver.name}
                        </div>
                      )}
                      <div className="party-line">
                        {printShipment.receiver?.email}
                      </div>
                      <div className="party-line">
                        {printShipment.receiver?.phone}
                      </div>
                      <div className="party-line address">
                        {printShipment.destinationAddress?.street}
                        <br />
                        {printShipment.destinationAddress?.city},{" "}
                        {printShipment.destinationAddress?.state}{" "}
                        {printShipment.destinationAddress?.postalCode}
                        <br />
                        {printShipment.destinationAddress?.country}
                      </div>
                    </InvoicePartyCard>
                  </InvoicePartyGrid>

                  {/* Shipment info */}
                  <InvoiceSection>
                    <h4>Shipment Information</h4>
                    <InvoiceInfoGrid>
                      <div className="cell">
                        <span className="cell-label">Carrier</span>
                        <span className="cell-value">
                          {printShipment.carrier || "Trans World Shipping"}
                        </span>
                      </div>
                      <div className="cell">
                        <span className="cell-label">Sent Date</span>
                        <span className="cell-value">
                          {formatDate(printShipment.sentDate)}
                        </span>
                      </div>
                      <div className="cell">
                        <span className="cell-label">Est. Delivery</span>
                        <span className="cell-value">
                          {formatDate(printShipment.estimatedDeliveryDate)}
                        </span>
                      </div>
                      <div className="cell">
                        <span className="cell-label">Weight</span>
                        <span className="cell-value">
                          {printShipment.weight?.value}{" "}
                          {printShipment.weight?.unit}
                        </span>
                      </div>
                      <div className="cell">
                        <span className="cell-label">Dimensions</span>
                        <span className="cell-value">
                          {printShipment.dimensions?.length} ×{" "}
                          {printShipment.dimensions?.width} ×{" "}
                          {printShipment.dimensions?.height}{" "}
                          {printShipment.dimensions?.unit}
                        </span>
                      </div>
                      <div className="cell">
                        <span className="cell-label">Flags</span>
                        <span className="cell-value">
                          {[
                            printShipment.isFragile && "Fragile",
                            printShipment.requiresSignature &&
                              "Signature Required",
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </span>
                      </div>
                      {printShipment.description && (
                        <div className="cell wide">
                          <span className="cell-label">Description</span>
                          <span className="cell-value">
                            {printShipment.description}
                          </span>
                        </div>
                      )}
                      {printShipment.specialInstructions && (
                        <div className="cell wide">
                          <span className="cell-label">
                            Special Instructions
                          </span>
                          <span className="cell-value">
                            {printShipment.specialInstructions}
                          </span>
                        </div>
                      )}
                    </InvoiceInfoGrid>
                  </InvoiceSection>

                  {/* Charges */}
                  <InvoiceCharges>
                    <div className="charge-row">
                      <span>Shipping Cost</span>
                      <span>
                        ${(printShipment.shippingCost || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="charge-row">
                      <span>Insurance</span>
                      <span>
                        ${(printShipment.insurance || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="charge-total">
                      <span>Total Amount</span>
                      <span>
                        ${getTotalPrice(printShipment).toLocaleString()}
                      </span>
                    </div>
                  </InvoiceCharges>
                </PrintModalBody>

                <PrintActions>
                  <button className="cancel-btn" onClick={closePrintModal}>
                    Close
                  </button>
                  <PrintButton onClick={handlePrint}>
                    <FaPrint /> Print Invoice
                  </PrintButton>
                </PrintActions>
              </>
            )}
          </PrintModalContent>
        </ModalOverlay>
      )}

      {/* Delete confirmation (replaces window.confirm) */}
      <ConfirmModal
        open={!!deleteTargetId}
        danger
        title="Delete this shipment?"
        message="This will permanently remove the shipment and its tracking history. This action cannot be undone."
        confirmLabel="Delete"
        loading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTargetId(null)}
      />

      {/* Notice popup (replaces alert) */}
      <ConfirmModal
        open={!!notice}
        alertOnly
        danger
        title={notice?.title}
        message={notice?.message}
        onClose={() => setNotice(null)}
      />
    </>
  );
};
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;

// Print Modal Styled Components
const PrintModalContent = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${fadeIn} 0.3s ease;

  @media screen and (max-width: 768px) {
    max-width: 95%;
  }
`;

const PrintModalBody = styled.div`
  padding: 2rem;

  @media print {
    padding: 0;
  }
`;

const InvoiceBrandHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #0b1e33;
  border-bottom: 4px solid #d4af37;
  padding: 1.25rem 1.5rem;
  border-radius: 20px 20px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;

  .brand {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;

    .brand-icon {
      font-size: 1.6rem;
      color: #d4af37;
      flex-shrink: 0;
    }

    h2 {
      font-size: 1.05rem;
      font-weight: 800;
      color: white;
      margin: 0;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    span {
      display: block;
      font-size: 0.68rem;
      color: #9fb0c3;
      letter-spacing: 0.04em;
      margin-top: 0.15rem;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    flex-shrink: 0;
  }

  .invoice-badge {
    background: #d4af37;
    color: #0b1e33;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 0.35rem 0.9rem;
    border-radius: 4px;
  }

  .close-x {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s;

    &:hover {
      color: #e53e3e;
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1rem;

    .brand span {
      display: none;
    }
  }
`;

const InvoiceMetaBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  flex-wrap: wrap;
  padding: 0.9rem 1.5rem;
  background: #f8f9fb;
  border-bottom: 1px solid #e2e8f0;

  .meta {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .meta-label {
    font-size: 0.62rem;
    font-weight: 700;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .meta-value {
    font-size: 0.85rem;
    font-weight: 700;
    color: #0b1e33;

    &.mono {
      font-family: "JetBrains Mono", "Courier New", monospace;
      letter-spacing: 0.03em;
    }
  }

  @media screen and (max-width: 600px) {
    gap: 1rem;
    padding: 0.75rem 1rem;
  }
`;

const InvoiceHeldStrip = styled.div`
  background: linear-gradient(135deg, #c53030, #9b2c2c);
  color: white;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
`;

const InvoicePartyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InvoicePartyCard = styled.div`
  border: 2px solid #0b1e33;
  border-radius: 10px;
  padding: 0.9rem 1rem;

  &.to {
    border-color: #d4af37;
  }

  .party-tag {
    display: inline-block;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: white;
    background: #0b1e33;
    padding: 0.15rem 0.6rem;
    border-radius: 3px;
    margin-bottom: 0.5rem;
  }

  &.to .party-tag {
    background: #d4af37;
    color: #0b1e33;
  }

  .party-name {
    font-size: 0.95rem;
    font-weight: 800;
    color: #0b1e33;
    margin-bottom: 0.2rem;
  }

  .party-line {
    font-size: 0.78rem;
    color: #4a5568;
    line-height: 1.5;
    word-break: break-word;

    &.address {
      margin-top: 0.4rem;
      padding-top: 0.4rem;
      border-top: 1px dashed #e2e8f0;
    }
  }
`;

const InvoiceSection = styled.div`
  margin-bottom: 1.5rem;

  h4 {
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #0b1e33;
    border-bottom: 2px solid #d4af37;
    padding-bottom: 0.35rem;
    margin: 0 0 0.75rem;
  }
`;

const InvoiceInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem 1rem;

  .cell {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;

    &.wide {
      grid-column: 1 / -1;
    }
  }

  .cell-label {
    font-size: 0.62rem;
    font-weight: 700;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .cell-value {
    font-size: 0.82rem;
    font-weight: 600;
    color: #0b1e33;
    word-break: break-word;
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InvoiceCharges = styled.div`
  background: #0b1e33;
  border-radius: 10px;
  padding: 1rem 1.25rem;

  .charge-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.82rem;
    padding: 0.3rem 0;

    span:first-child {
      color: #9fb0c3;
    }

    span:last-child {
      color: white;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
  }

  .charge-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
    padding-top: 0.6rem;
    border-top: 1px solid rgba(255, 255, 255, 0.15);

    span:first-child {
      font-size: 0.72rem;
      font-weight: 800;
      color: #d4af37;
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }

    span:last-child {
      font-size: 1.25rem;
      font-weight: 800;
      color: #d4af37;
      font-variant-numeric: tabular-nums;
    }
  }
`;

const PrintActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  gap: 0.75rem;

  .cancel-btn {
    padding: 0.6rem 1.4rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: inherit;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f8f9fa;
      border-color: #cbd5e0;
    }
  }

  @media screen and (max-width: 600px) {
    flex-direction: column-reverse;

    .cancel-btn,
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: #d4af37;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c09b28;
    transform: translateY(-1px);
  }
`;

// ... rest of the existing styled components remain the same
const spin = keyframes`to { transform: rotate(360deg); }`;

const Shell = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f0f2f5;
  font-family: "DM Sans", "Segoe UI", sans-serif;
`;

const Sidebar = styled.aside`
  width: 240px;
  background: #0b1e33;
  color: #fff;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 50;
  overflow: hidden;

  &.closed {
    width: 64px;
  }

  .sb-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    white-space: nowrap;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    overflow: hidden;

    span {
      font-size: 1rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
  }

  .brand-icon {
    font-size: 1.4rem;
    color: #d4af37;
    flex-shrink: 0;
  }

  .toggle-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    font-size: 1rem;
    flex-shrink: 0;
    padding: 0.2rem;
    transition: color 0.2s;

    &:hover {
      color: #d4af37;
    }
  }

  .sb-nav {
    flex: 1;
    padding: 1.25rem 0.5rem;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.7rem 0.75rem;
    background: none;
    border: none;
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.55);
    cursor: pointer;
    font-size: 0.9rem;
    font-family: inherit;
    white-space: nowrap;
    transition: all 0.2s ease;

    svg {
      font-size: 1.05rem;
      flex-shrink: 0;
    }

    &:hover {
      background: rgba(212, 175, 55, 0.1);
      color: #d4af37;
    }

    &.active {
      background: rgba(212, 175, 55, 0.15);
      color: #d4af37;
      font-weight: 600;
      border-left: 3px solid #d4af37;
    }

    &.logout:hover {
      background: rgba(229, 62, 62, 0.15);
      color: #e55353;
    }
  }

  .sb-footer {
    padding: 0.75rem 0.5rem 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
  }

  @media screen and (max-width: 900px) {
    position: fixed;
    transform: translateX(-100%);

    &.open {
      transform: translateX(0);
    }
  }
`;

const Main = styled.main`
  flex: 1;
  padding: 2rem 2.5rem;
  min-width: 0;
  animation: ${fadeIn} 0.4s ease both;

  &.expanded {
    margin-left: 0;
  }

  @media screen and (max-width: 1200px) {
    padding: 1.5rem 2rem;
  }

  @media screen and (max-width: 900px) {
    padding: 1.25rem 1rem;
  }

  @media screen and (max-width: 600px) {
    padding: 1rem 0.75rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  gap: 1rem;
  flex-wrap: wrap;

  .title-block h1 {
    font-size: 1.75rem;
    font-weight: 800;
    color: #0b1e33;
    margin: 0 0 0.15rem;
    letter-spacing: -0.03em;
  }

  .title-block p {
    font-size: 0.82rem;
    color: #8a9ab0;
    margin: 0;
  }

  @media screen and (max-width: 600px) {
    .title-block h1 {
      font-size: 1.4rem;
    }
  }
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.4rem;
  background: #d4af37;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 14px rgba(212, 175, 55, 0.35);
  transition: all 0.22s ease;

  &:hover {
    background: #c09b28;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(212, 175, 55, 0.45);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 0.8rem;
  }

  @media screen and (max-width: 420px) {
    padding: 0.55rem 1rem;
    font-size: 0.8rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;

  @media screen and (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 200px;
  position: relative;

  .search-icon {
    position: absolute;
    left: 0.9rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
    font-size: 0.85rem;
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 0.65rem 1rem 0.65rem 2.4rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    font-size: 0.88rem;
    font-family: inherit;
    color: #0b1e33;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #b0bec5;
    }

    &:focus {
      border-color: #d4af37;
    }
  }
`;

const FilterDropdown = styled.div`
  position: relative;

  .filter-trigger {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 1rem;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.88rem;
    font-family: inherit;
    color: #4a5568;
    cursor: pointer;
    transition: border-color 0.2s;
    white-space: nowrap;

    svg {
      font-size: 0.7rem;
      transition: transform 0.2s;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &:hover {
      border-color: #d4af37;
    }
  }

  .filter-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    min-width: 160px;
    z-index: 20;
    list-style: none;
    padding: 0.4rem;
    margin: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.55rem 0.75rem;
      border-radius: 7px;
      font-size: 0.85rem;
      cursor: pointer;
      color: #4a5568;
      transition: background 0.15s;

      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      &:hover {
        background: #f8f9fa;
      }

      &.selected {
        background: rgba(212, 175, 55, 0.1);
        color: #d4af37;
        font-weight: 600;
      }
    }
  }
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 560px;
  }

  thead tr {
    background: #f8f9fb;
  }

  th {
    padding: 0.9rem 1.25rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 700;
    color: #8a9ab0;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    white-space: nowrap;
  }

  td {
    padding: 0.9rem 1.25rem;
    font-size: 0.88rem;
    color: #4a5568;
    border-bottom: 1px solid #f0f2f5;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr {
    transition: background 0.15s;

    &:hover {
      background: #fafbfc;
    }
  }

  .row-num {
    color: #b0bec5;
    font-size: 0.8rem;
    width: 40px;
  }

  .tracking {
    font-family: "JetBrains Mono", "Fira Code", monospace;
    font-size: 0.82rem;
    font-weight: 600;
    color: #0b1e33;
    letter-spacing: 0.03em;
  }

  .sender {
    font-weight: 600;
    color: #0b1e33;
  }

  .empty-row {
    text-align: center;
    color: #a0aec0;
    padding: 3rem 1rem;
    font-size: 0.9rem;
  }

  @media screen and (max-width: 600px) {
    th,
    td {
      padding: 0.75rem 0.9rem;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(p) => p.color};
  background: ${(p) => p.bg};
  white-space: nowrap;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.color};
    flex-shrink: 0;
  }
`;

const ACTION_COLORS = {
  view: {
    color: "#2196f3",
    bg: "rgba(33,150,243,0.1)",
    hover: "rgba(33,150,243,0.2)",
  },
  edit: {
    color: "#d4af37",
    bg: "rgba(212,175,55,0.1)",
    hover: "rgba(212,175,55,0.2)",
  },
  delete: {
    color: "#e53e3e",
    bg: "rgba(229,62,62,0.1)",
    hover: "rgba(229,62,62,0.2)",
  },
};

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;

  @media screen and (max-width: 480px) {
    gap: 0.3rem;
  }
`;

const ActionBtn = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease;
  color: ${(p) => ACTION_COLORS[p.$variant].color};
  background: ${(p) => ACTION_COLORS[p.$variant].bg};

  &:hover {
    background: ${(p) => ACTION_COLORS[p.$variant].hover};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media screen and (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.72rem;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1rem;
  border-top: 1px solid #f0f2f5;
  flex-wrap: wrap;

  button {
    min-width: 34px;
    height: 34px;
    padding: 0 0.5rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    color: #4a5568;
    font-size: 0.82rem;
    font-family: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.18s;

    &:hover:not(:disabled) {
      border-color: #d4af37;
      color: #d4af37;
    }

    &.active {
      background: #d4af37;
      border-color: #d4af37;
      color: #fff;
      font-weight: 700;
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  @media screen and (max-width: 480px) {
    gap: 0.3rem;
    padding: 0.75rem;

    button {
      min-width: 28px;
      height: 28px;
      font-size: 0.75rem;
    }
  }
`;

const SpinnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;

  .spinner {
    width: 44px;
    height: 44px;
    border: 3px solid #e2e8f0;
    border-top-color: #d4af37;
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite;
  }

  p {
    font-size: 0.88rem;
    color: #a0aec0;
    margin: 0;
  }
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 0.75rem;
  text-align: center;

  .err-icon {
    font-size: 2rem;
    color: #e53e3e;
    opacity: 0.7;
  }

  p {
    font-size: 0.88rem;
    color: #718096;
    max-width: 320px;
    margin: 0;
    line-height: 1.5;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.5rem;
    padding: 0.55rem 1.2rem;
    background: #d4af37;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-family: inherit;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #c09b28;
    }

    svg {
      font-size: 0.75rem;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${fadeIn} 0.3s ease;

  @media screen and (max-width: 768px) {
    max-width: 95%;
    max-height: 95vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0b1e33;
    margin: 0;
  }

  @media screen and (max-width: 768px) {
    padding: 1rem;

    h2 {
      font-size: 1.1rem;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  cursor: pointer;
  color: #a0aec0;
  transition: color 0.2s;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #e53e3e;
  }

  @media screen and (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;

  @media screen and (max-width: 768px) {
    padding: 1rem;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #0b1e33;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;

  svg {
    color: #d4af37;
    font-size: 0.9rem;
  }

  @media screen and (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: #4a5568;
  word-break: break-word;

  @media screen and (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const StatusBadgeSmall = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
  background: ${(p) => STATUS_META[p.$status]?.bg || "rgba(142,142,147,0.12)"};
  color: ${(p) => STATUS_META[p.$status]?.color || "#8E8E93"};

  @media screen and (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const AddressBox = styled.div`
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 10px;

  @media screen and (max-width: 768px) {
    padding: 0.6rem;
  }
`;

const AddressTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: #d4af37;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddressText = styled.div`
  font-size: 0.8rem;
  color: #4a5568;
  line-height: 1.4;

  @media screen and (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const PartiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const PartyBox = styled.div`
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 10px;

  @media screen and (max-width: 768px) {
    padding: 0.6rem;
  }
`;

const PartyTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: #d4af37;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PartyDetail = styled.div`
  font-size: 0.8rem;
  color: #4a5568;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  word-break: break-word;

  svg {
    font-size: 0.7rem;
    color: #a0aec0;
    flex-shrink: 0;
  }

  @media screen and (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const SpecialInstructions = styled.div`
  background: #fef3c7;
  padding: 0.75rem;
  border-radius: 10px;
  font-size: 0.8rem;
  color: #92400e;
  line-height: 1.5;

  @media screen and (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.6rem;
  }
`;

const TrackingTimeline = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const TimelineLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 20px;
`;

const TimelineDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(p) =>
    p.$stopover ? "#2196f3" : p.$isFirst ? "#d4af37" : "#e2e8f0"};
  border: 2px solid
    ${(p) => (p.$stopover ? "#2196f3" : p.$isFirst ? "#d4af37" : "#c4cdd6")};
  box-shadow: ${(p) =>
    p.$stopover
      ? "0 0 0 3px rgba(33,150,243,0.2)"
      : p.$isFirst
        ? "0 0 0 3px rgba(212,175,55,0.2)"
        : "none"};
  flex-shrink: 0;
  margin-top: 3px;
  transition: all 0.2s;
`;

const TimelineConnector = styled.div`
  width: 2px;
  flex: 1;
  min-height: 28px;
  background: linear-gradient(to bottom, #d4af37 0%, #e2e8f0 100%);
  margin: 3px 0;
`;

const TimelineCard = styled.div`
  flex: 1;
  background: ${(p) =>
    p.$stopover
      ? "rgba(33,150,243,0.06)"
      : p.$isFirst
        ? "rgba(212,175,55,0.06)"
        : "#fafbfc"};
  border: 1px solid
    ${(p) =>
      p.$stopover
        ? "rgba(33,150,243,0.3)"
        : p.$isFirst
          ? "rgba(212,175,55,0.3)"
          : "#e2e8f0"};
  border-radius: 10px;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.5rem;
`;

const TimelineStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => (p.$isFirst ? "#b8860b" : "#0b1e33")};
  margin-bottom: 0.2rem;

  .stopover-icon {
    color: #2196f3;
    font-size: 0.85rem;
  }
`;

const TimelineDesc = styled.div`
  font-size: 0.75rem;
  color: #718096;
  line-height: 1.4;
  margin-bottom: 0.25rem;
`;

const TimelineAddress = styled.div`
  font-size: 0.75rem;
  color: #0b1e33;
  line-height: 1.4;
  margin-bottom: 0.35rem;
  padding: 0.4rem 0.6rem;
  background: rgba(33, 150, 243, 0.08);
  border-radius: 6px;
`;

const TimelineDate = styled.div`
  font-size: 0.68rem;
  color: #a0aec0;
  font-variant-numeric: tabular-nums;
`;

const RouteControlBox = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  .route-status {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    margin: 0;

    &.paused {
      color: #ff9f0a;
    }
  }

  .route-error {
    font-size: 0.78rem;
    color: #e53e3e;
    margin: 0;
  }
`;

const RouteFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;

  input {
    width: 100%;
    padding: 0.55rem 0.7rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.82rem;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #d4af37;
    }
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RouteInput = styled.input`
  width: 100%;
  padding: 0.55rem 0.7rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.82rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #d4af37;
  }
`;

const RouteFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;

  @media screen and (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const CancelRouteBtn = styled.button`
  padding: 0.55rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #f0f2f5;
  }
`;

const RouteActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  color: white;
  background: ${(p) => (p.$variant === "pause" ? "#ff9f0a" : "#2196f3")};
  transition: all 0.2s;
  align-self: flex-start;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #d4af37;
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite;
  }

  p {
    font-size: 0.85rem;
    color: #a0aec0;
  }
`;

const ModalError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  gap: 0.75rem;

  svg {
    font-size: 2rem;
    color: #e53e3e;
  }

  p {
    font-size: 0.85rem;
    color: #718096;
  }

  button {
    padding: 0.5rem 1rem;
    background: #d4af37;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.85rem;

    &:hover {
      background: #c09b28;
    }
  }
`;

export default AdminDashboard;
